import { ProjectRepresentative, ReportMetadata } from '../types';

/**
 * Verantwortliche eines Projekts: pure Lese-/Schreiblogik über
 * `metadata.representatives` mit Ableitung aus den Legacy-Feldern
 * `coordinator*` / `coordinatorDeputy*` / `coordinatorDeputy2*`.
 *
 * Quelle der Wahrheit ist das Array; Position 0 und die ersten beiden
 * Vertreter werden beim Schreiben per `applyRepresentatives` in die
 * Legacy-Felder gespiegelt, damit PDFs, Vorankündigung und alte Clients
 * unverändert weiterlaufen. Verantwortliche Planungsphase (Einträge ab
 * Position 1 mit `bereich: 'planung'`) stehen nur im Array — siehe
 * `istPlanungsVerantwortlicher`.
 * Kein Datenbank-Backfill: Alt-Projekte werden erst beim ersten Speichern
 * der Verantwortlichkeiten materialisiert.
 *
 * Wer im ARRAY steht, ist zuständig — dort gibt es keinen Aktiv-Haken je
 * Eintrag. Für die Ableitung aus den LEGACY-Feldern gilt das nicht: dort
 * entscheidet weiterhin `coordinatorDeputyActive` / `...2Active`, weil der
 * Altbestand seine Zuständigkeits-Information genau darin trägt (siehe
 * `legacyDeputyActive`).
 */

const text = (value: string | undefined | null): string => (value || '').trim();

/**
 * Leere Platzhalter-Einträge fliegen raus. Ein Name allein genügt, sonst
 * müssen id UND E-Mail zusammen dastehen.
 *
 * Warum nicht mehr „E-Mail allein genügt": Das alte Stammdaten-Formular leerte
 * beim Entfernen eines Stellvertreters nur das Namensfeld und ließ
 * `coordinatorDeputyEmail` stehen. Diese Karteileichen sind der Grund, warum
 * Kollegen in Projekten als Vertreter auftauchen, in denen sie nie zuständig
 * waren — und seit dem Wegfall des Aktiv-Hakens schlagen sie alle gleichzeitig
 * durch. Ein Slot mit id + E-Mail ist dagegen nachweislich einmal aus einem
 * Profil befüllt worden und bleibt gültig.
 */
const hasContent = (rep: ProjectRepresentative): boolean =>
    !!(text(rep.name) || (text(rep.id) && text(rep.email)));

/**
 * Position 0 ist der Hauptverantwortliche und fällt schon bei id ODER E-Mail
 * nicht weg — anders als die Stellvertreter-Slots darunter.
 *
 * Warum die Ausnahme: Ein Alt-Projekt kann `coordinatorId` tragen, während
 * Name und E-Mail leer sind. Nach der Namensregel oben verschwände dieser Slot,
 * der Stellvertreter rutschte auf Index 0 — und `applyRepresentatives`
 * überschriebe beim nächsten Speichern `coordinator`/`coordinatorId` mit ihm.
 * Der bisherige Besitzer verlöre seinen RLS-Zweig `coordinatorId = auth.uid()`
 * und auf sämtlichen PDFs stünde ab da ein anderer Koordinatorname. Dieses
 * Rutschen ist zerstörend und irreversibel, eine namenlose Position 0 dagegen
 * nur unschön.
 *
 * Bewusst NUR für Position 0: Die Karteileichen-Regel für die Deputy-Slots —
 * ein Name allein oder id UND E-Mail — bleibt unangetastet, sie ist der Kern
 * des Auftrags. Ein Koordinator-Slot mit bloßer E-Mail ist zudem kein Fund aus
 * dem alten Stammdaten-Formular: das leerte den Namen von Stellvertretern,
 * nicht den des Hauptverantwortlichen.
 */
const hasPrimaryContent = (rep: ProjectRepresentative): boolean =>
    !!(text(rep.name) || text(rep.id) || text(rep.email));

/** Behält Position 0 nach der Koordinatorregel, alles darunter nach der strengen. */
const keepAt = (rep: ProjectRepresentative, index: number): boolean =>
    !!rep && (index === 0 ? hasPrimaryContent(rep) : hasContent(rep));

/** Trimmt und entfernt leere Felder — nie ein `||`-Fallback auf Altwerte. */
const normalize = (rep: ProjectRepresentative): ProjectRepresentative => {
    const out: ProjectRepresentative = { name: text(rep.name) };
    const id = text(rep.id);
    if (id) out.id = id;
    const email = text(rep.email);
    if (email) out.email = email;
    const phone = text(rep.phone);
    if (phone) out.phone = phone;
    if (Array.isArray(rep.weekdays)) out.weekdays = [...rep.weekdays];
    // Ohne diese Zeile verwürfe jedes Speichern der Vertreterliste die
    // Bereichs-Einteilung aus dem Anlage-Assistenten.
    if (rep.bereich === 'planung' || rep.bereich === 'ausfuehrung') out.bereich = rep.bereich;
    return out;
};

/**
 * Entfernt Mehrfachnennungen derselben Person. Die frühere Fundstelle gewinnt,
 * Index 0 also immer — wer Hauptverantwortlicher ist, darf sich nicht zusätzlich
 * als eigener Stellvertreter wiederfinden.
 *
 * Warum drei Schlüssel: Der Koordinator-Slot trägt id + E-Mail, der gespiegelte
 * Deputy-Slot derselben Person oft nur die E-Mail — ein Vergleich allein über
 * die id würde das Duplikat durchlassen. Der Name greift nur als letzte Instanz,
 * wenn weder id noch E-Mail da sind; zwei echte Namensvettern ohne jeden Kontakt
 * sind der seltenere Fehlerfall als die Karteileiche.
 */
const dedupe = (list: ProjectRepresentative[]): ProjectRepresentative[] => {
    const seenIds = new Set<string>();
    const seenEmails = new Set<string>();
    const seenNames = new Set<string>();

    return list.filter(rep => {
        const id = text(rep.id);
        const email = text(rep.email).toLowerCase();
        const name = text(rep.name).toLowerCase();

        if (id && seenIds.has(id)) return false;
        if (email && seenEmails.has(email)) return false;
        if (!id && !email && name && seenNames.has(name)) return false;

        if (id) seenIds.add(id);
        if (email) seenEmails.add(email);
        if (name) seenNames.add(name);
        return true;
    });
};

/** Die beiden Legacy-Stellvertreter-Slots als Eintrag — ohne Aktiv-Prüfung. */
const legacyDeputySlot = (metadata: ReportMetadata, slot: 1 | 2): ProjectRepresentative =>
    slot === 1
        ? {
              id: metadata.coordinatorDeputyId,
              name: metadata.coordinatorDeputy || '',
              email: metadata.coordinatorDeputyEmail,
              phone: metadata.coordinatorDeputyPhone,
              weekdays: metadata.coordinatorDeputyWeekdays,
          }
        : {
              id: metadata.coordinatorDeputy2Id,
              name: metadata.coordinatorDeputy2 || '',
              email: metadata.coordinatorDeputy2Email,
              phone: metadata.coordinatorDeputy2Phone,
              weekdays: metadata.coordinatorDeputy2Weekdays,
          };

/**
 * Zählt ein Legacy-Stellvertreter-Slot als zuständig? NUR mit gesetztem
 * Aktiv-Haken.
 *
 * Der Umbau „gelistet = zuständig" hat den Haken aus der Oberfläche entfernt —
 * für NEUE Daten im `representatives`-Array bleibt das richtig, dort gibt es
 * kein Flag. Der Altbestand kodiert seine Zuständigkeit aber genau in diesem
 * Feld: Die PROD-Diagnose zeigt dutzende Slots mit vollständigem Namen, E-Mail
 * UND id, bei denen `coordinatorDeputyActive` bewusst auf false steht (Projekt
 * 26031014 trägt sogar Deputy 1 inaktiv und Deputy 2 aktiv). Ohne diese Prüfung
 * gilt jeder je eingetragene Vertreter als zuständig — daher die Beschwerde
 * über Projektfluten mit fremden „zweiter Ansprechpartner"-Zuordnungen.
 *
 * Fehlendes Flag (undefined/null, nie gesetzt) = INAKTIV. Das ist die Semantik
 * der erprobten Vorgänger-Fassung, kein dritter Weg:
 *   - `git show d957fa4^:src/hooks/useProjectData.ts` prüfte
 *     `meta?.coordinatorDeputyActive && dep1Email.length > 3 && ...` —
 *     undefined ist falsy, der Slot fiel raus.
 *   - `supabase/rls_hardening.sql:56-61` prüfte
 *     `coalesce((...->>'coordinatorDeputyActive')::boolean, false)` —
 *     fehlendes Feld wurde ausdrücklich zu false.
 * Beide Seiten haben ein fehlendes Flag also als „nicht zuständig" gewertet.
 */
const legacyDeputyActive = (flag: boolean | undefined | null): boolean => flag === true;

/**
 * Alle Verantwortlichen in Prioritätsreihenfolge (Index 0 = Hauptverantwortlicher).
 * Ohne Array wird aus den Legacy-Feldern abgeleitet.
 */
export const getRepresentatives = (
    metadata: ReportMetadata | undefined | null
): ProjectRepresentative[] => {
    if (!metadata) return [];

    const stored = Array.isArray(metadata.representatives) ? metadata.representatives : null;
    if (stored && stored.length > 0) {
        // Array-Pfad: kein Aktiv-Flag, „gelistet = zuständig" gilt unverändert.
        return dedupe(stored.filter(keepAt).map(normalize));
    }

    const legacy: ProjectRepresentative[] = [
        {
            id: metadata.coordinatorId,
            name: metadata.coordinator,
            email: metadata.coordinatorEmail,
            phone: metadata.coordinatorPhone,
        },
    ];
    // Position 0 (Koordinator) hat kein Aktiv-Flag und bleibt unberührt.
    if (legacyDeputyActive(metadata.coordinatorDeputyActive)) legacy.push(legacyDeputySlot(metadata, 1));
    if (legacyDeputyActive(metadata.coordinatorDeputy2Active)) legacy.push(legacyDeputySlot(metadata, 2));

    return dedupe(legacy.filter(keepAt).map(normalize));
};

/**
 * Ist dieser Eintrag eine „Verantwortliche Planungsphase"?
 *
 * Bewusst nur am Feld festgemacht, nicht an der Position: Position 0 ist
 * immer der Hauptverantwortliche und zählt zur Ausführung, auch wenn ihr
 * Eintrag (etwa nach einer Übernahme) noch `bereich: 'planung'` trägt — das
 * regeln die Listenfunktionen darunter über den Index. Einträge ohne Feld
 * (Freitext-Altdaten, alles vor dem Kickoff-Umbau) gelten für beide Bereiche
 * und sind damit gerade KEINE reinen Planungs-Verantwortlichen.
 */
export const istPlanungsVerantwortlicher = (
    rep: ProjectRepresentative | null | undefined
): boolean => !!rep && rep.bereich === 'planung';

/**
 * Position 0 immer, dazu alle Einträge, die KEINE Planungs-Verantwortlichen
 * sind — wer Begehungen macht. Reihenfolge wie `getRepresentatives`.
 *
 * Die Planungsperson ist für Vorankündigung, Unterlage, SiGe-Plan, A+S-Plan
 * und Planung zuständig, nicht für Begehungen. Tourenplanung, Auslastung und
 * Begehungstage je Vertreter dürfen sie deshalb nicht mitzählen; Zugriff hat
 * sie trotzdem (RLS-Array-Zweig, `getProjectRelation`).
 */
export const getAusfuehrungsVertreter = (
    metadata: ReportMetadata | undefined | null
): ProjectRepresentative[] =>
    getRepresentatives(metadata).filter((rep, idx) => idx === 0 || !istPlanungsVerantwortlicher(rep));

/** Nur die Verantwortlichen Planungsphase (ab Position 1, bereich 'planung'), Reihenfolge wie in der Liste. */
export const getPlanungsVerantwortliche = (
    metadata: ReportMetadata | undefined | null
): ProjectRepresentative[] =>
    getRepresentatives(metadata).filter((rep, idx) => idx > 0 && istPlanungsVerantwortlicher(rep));

/** Dieselbe Person? id/E-Mail zuerst, Name nur als letzte Instanz. */
const sameParty = (a: ProjectRepresentative, b: ProjectRepresentative): boolean => {
    if (matchesRepresentative(a, { id: b.id, email: b.email })) return true;
    const nameA = text(a.name).toLowerCase();
    return !!nameA && nameA === text(b.name).toLowerCase();
};

/**
 * Schreibt die Liste und spiegelt die ersten Einträge in die Legacy-Felder.
 * Fehlende Positionen leeren ihren Spiegel — sonst blieben Geister-
 * Stellvertreter in PDFs und Sichtbarkeitsprüfungen stehen.
 *
 * Verantwortliche Planungsphase werden NICHT gespiegelt, nur die übrigen
 * Vertreter rücken in die beiden Deputy-Slots. Die Altfelder bedeuten
 * „Vertretung bei Begehungen": Der Begehungsbericht druckt sie als
 * Stellvertreter (`generatorStandard.ts`), der Besetzt-Spiegel setzt
 * `coordinatorDeputyActive = true` und der Buchungsdialog etikettiert die
 * Person als „Vertretung". Für jemanden, der nur die Planung verantwortet,
 * ist alles drei falsch. Der Zugriff hängt daran nicht — den gibt der
 * Array-Zweig von `can_access_project`. Ein früher noch gespiegelter
 * Planungs-Slot wird beim nächsten Speichern frei und geleert bzw. vom
 * nächsten Vertreter belegt (siehe `dormant`, Bedingung 1 und 3).
 *
 * AUSNAHME: ein „schlafender" Legacy-Slot (siehe `dormant`) bleibt Byte für
 * Byte stehen. Er ist nicht gelöscht worden, sondern von `getRepresentatives`
 * wegen `...Active = false` gar nicht erst gezeigt worden — sein Fehlen in der
 * Liste ist deshalb kein Löschbefehl. Ohne diese Ausnahme würde das nächste
 * Speichern (der Wochentags-Klick in den Projektkennzahlen genügt) Name,
 * E-Mail und id einer bewusst deaktivierten Vertretung wegwerfen, bevor der
 * Betreiber über den Altbestand entscheiden konnte.
 */
export const applyRepresentatives = (
    metadata: ReportMetadata,
    reps: ProjectRepresentative[]
): ReportMetadata => {
    // Dieselbe Regel wie beim Lesen — sonst verwürfe das Schreiben genau den
    // Koordinator-Slot wieder, den getRepresentatives eben gerettet hat.
    const list = dedupe((reps || []).filter(keepAt).map(normalize));
    const [first] = list;

    /**
     * Besetzt-Spiegel nur für einen namentlichen Stellvertreter.
     *
     * Bewusst NICHT `!!second`: Ein beliebiges Speichern (z.B. der Wochentags-
     * Klick in den Projektkennzahlen) hätte sonst jeden Alt-Slot dauerhaft in
     * der Datenbank reaktiviert — genau so verbreiteten sich die Karteileichen.
     * Und bewusst NICHT „Altwert unangetastet lassen": Der Spiegel würde dann
     * vom Slot-Inhalt abweichen und ein neu eingetragener Stellvertreter bliebe
     * für Alt-Clients und die Legacy-RLS unsichtbar. Der Name ist das Kriterium,
     * weil nur ein benannter Eintrag nachweislich gepflegt wurde.
     */
    const occupied = (rep: ProjectRepresentative | undefined): boolean => !!rep && !!text(rep.name);

    /**
     * Schläft dieser Legacy-Slot? Dann nicht anfassen.
     *
     * Drei Bedingungen, alle nötig:
     *  1. Aktiv-Haken NICHT true — nur so ein Slot wurde beim Lesen ausgeblendet.
     *     Ein aktiver Slot, der nicht mehr in der Liste steht, ist echt gelöscht
     *     worden und wird wie bisher geleert.
     *  2. Der Slot hat Substanz (`hasContent`). Die Karteileiche des alten
     *     Formulars — E-Mail ohne Namen und ohne id — ist keine erhaltenswerte
     *     Information und verschwindet weiterhin.
     *  3. Die Person steht nicht in der neuen Liste. Sonst schriebe der Spiegel
     *     sie ohnehin und wir hätten sie doppelt. Bewusst gegen die GANZE
     *     Liste, auch gegen die Planungs-Verantwortlichen, die gar nicht
     *     gespiegelt werden: Wer als Planungsperson eingetragen ist, über den
     *     hat der Betreiber entschieden. Bliebe sein alter Slot schlafend
     *     stehen, druckte der Begehungsbericht ihn weiter als Stellvertreter
     *     (das PDF prüft den Aktiv-Haken nicht). Dasselbe gilt für einen
     *     früher gespiegelten Planungs-Slot ohne Namen, der mit `false`
     *     gespeichert wurde — er wird frei statt schlafend.
     */
    const dormant = (slot: 1 | 2): boolean => {
        const flag = slot === 1 ? metadata.coordinatorDeputyActive : metadata.coordinatorDeputy2Active;
        if (legacyDeputyActive(flag)) return false;
        const old = legacyDeputySlot(metadata, slot);
        if (!hasContent(old)) return false;
        return !list.some(rep => sameParty(rep, old));
    };

    /*
     * Slot-Vergabe: schlafende Slots bleiben reserviert, die Stellvertreter aus
     * der Liste rücken der Reihe nach in die freien. Bleibt kein freier Slot
     * (beide Alt-Slots schlafen), wird nicht gespiegelt — die Liste selbst ist
     * die Quelle der Wahrheit, und ein Überschreiben wäre genau der Datenverlust,
     * den diese Regel verhindern soll.
     *
     * Planungs-Verantwortliche zählen hier nicht mit: Ein Vertreter hinter
     * ihnen rückt in Slot 1 vor, statt hinter einer Lücke zu landen.
     */
    const deputies = list.slice(1).filter(rep => !istPlanungsVerantwortlicher(rep));
    const asleep = { 1: dormant(1), 2: dormant(2) } as const;
    const free: (1 | 2)[] = ([1, 2] as const).filter(slot => !asleep[slot]);
    const at = (slot: 1 | 2): ProjectRepresentative | undefined => {
        const pos = free.indexOf(slot);
        return pos < 0 ? undefined : deputies[pos];
    };

    // Ein leeres Objekt lässt den `...metadata`-Spread stehen: der schlafende
    // Slot behält Name, E-Mail, id, Telefon, Wochentage und sein `false`.
    const slot1 = asleep[1]
        ? {}
        : {
              coordinatorDeputy: at(1) ? at(1)!.name : '',
              coordinatorDeputyId: at(1)?.id,
              coordinatorDeputyEmail: at(1)?.email,
              coordinatorDeputyPhone: at(1)?.phone,
              coordinatorDeputyActive: occupied(at(1)),
              coordinatorDeputyWeekdays: at(1)?.weekdays,
          };

    const slot2 = asleep[2]
        ? {}
        : {
              coordinatorDeputy2: at(2) ? at(2)!.name : '',
              coordinatorDeputy2Id: at(2)?.id,
              coordinatorDeputy2Email: at(2)?.email,
              coordinatorDeputy2Phone: at(2)?.phone,
              coordinatorDeputy2Active: occupied(at(2)),
              coordinatorDeputy2Weekdays: at(2)?.weekdays,
          };

    return {
        ...metadata,
        representatives: list,

        coordinator: first ? first.name : '',
        coordinatorId: first?.id,
        coordinatorEmail: first?.email,
        coordinatorPhone: first?.phone,

        ...slot1,
        ...slot2,
    };
};

/**
 * Gibt es mindestens einen Stellvertreter (Position > 0) mit verwertbarem
 * Kontakt? Steuert das „Gemeinsam"-Abzeichen der Projektlisten.
 *
 * Nur Ausführungs-Vertreter zählen: „Gemeinsam" heißt, die Begehungen teilen
 * sich mehrere. Eine reine Planungsperson macht keine Begehungen — mit ihr
 * trüge sonst jedes Kickoff-Projekt das Abzeichen.
 */
export const hasDeputy = (metadata: ReportMetadata | undefined | null): boolean =>
    getAusfuehrungsVertreter(metadata).some(
        (rep, idx) => idx > 0 && (text(rep.email).length > 3 || !!text(rep.id))
    );

/** Verschiebt einen Eintrag um eine Position; an den Enden passiert nichts. */
export const moveRepresentative = (
    reps: ProjectRepresentative[],
    index: number,
    direction: -1 | 1
): ProjectRepresentative[] => {
    const target = index + direction;
    if (index < 0 || index >= reps.length || target < 0 || target >= reps.length) return reps;
    const next = [...reps];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
};

/** Passt ein Eintrag zu diesem Kollegen? id gewinnt, sonst E-Mail (case-insensitiv). */
export const matchesRepresentative = (
    rep: ProjectRepresentative | undefined | null,
    user: { id?: string | null; email?: string | null } | undefined | null
): boolean => {
    if (!rep || !user) return false;
    const userId = text(user.id);
    if (userId && text(rep.id) === userId) return true;
    const userMail = text(user.email).toLowerCase();
    // Länge > 3: eine leere oder unsinnige Adresse darf niemanden berechtigen.
    return userMail.length > 3 && text(rep.email).toLowerCase() === userMail;
};

/**
 * Setzt eine Person an Position 0 (Projektübernahme). Ein vorhandener Eintrag
 * derselben Person wird ersetzt, der bisherige Hauptverantwortliche rutscht
 * auf Position 1 — er wird nie stillschweigend entfernt.
 */
export const promoteRepresentative = (
    reps: ProjectRepresentative[],
    rep: ProjectRepresentative
): ProjectRepresentative[] => {
    const promotedName = text(rep.name).toLowerCase();

    const rest = (reps || []).filter(r => {
        if (!r) return false;
        if (matchesRepresentative(r, { id: rep.id, email: rep.email })) return false;

        // Letzte Instanz Name: Ein Alt-Eintrag ohne id und ohne brauchbare
        // E-Mail lässt sich anders gar nicht wiedererkennen — er bliebe stehen
        // und die übernehmende Person sähe sich anschließend selbst als ihren
        // eigenen Stellvertreter. Der Namensvergleich greift deshalb nur dort,
        // wo id und E-Mail nichts hergeben, damit Namensgleichheit nie einen
        // eindeutig identifizierten Kollegen aus der Liste wirft.
        const identifiable = !!text(r.id) || text(r.email).length > 3;
        if (identifiable) return true;
        return !(promotedName && text(r.name).toLowerCase() === promotedName);
    });

    return dedupe([normalize(rep), ...rest.map(normalize)]);
};

/** Minimalprofil für den Kontaktabgleich — bewusst keine Kopplung an `UserProfile`. */
export interface RepresentativeProfile {
    id?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    phone?: string | null;
}

const profileName = (profile: RepresentativeProfile): string =>
    text(`${text(profile.first_name)} ${text(profile.last_name)}`);

/**
 * Steht der Eintrag nachweislich für genau dieses Profil?
 *
 * Die id allein genügt NICHT. Der CSV-Import stempelte jahrelang die id des
 * importierenden Admins in `coordinatorId` (siehe
 * supabase/diag_vertreter_karteileichen.sql, Befund (f)) — ein Abgleich allein
 * über die id schriebe dort die Kontaktdaten eines Wildfremden in das Projekt.
 * Deshalb muss zusätzlich der Name ODER die E-Mail zum Profil passen; erst
 * dann ist belegt, dass Eintrag und Profil dieselbe Person meinen.
 */
const confirmsProfile = (rep: ProjectRepresentative, profile: RepresentativeProfile): boolean => {
    const repName = text(rep.name).toLowerCase();
    if (repName && repName === profileName(profile).toLowerCase()) return true;
    const repMail = text(rep.email).toLowerCase();
    return repMail.length > 3 && repMail === text(profile.email).toLowerCase();
};

/**
 * Zieht Telefon und E-Mail der Verantwortlichen aus ihren Profilen nach.
 *
 * Warum es das braucht: Die Kontaktdaten werden beim Eintragen EINMAL aus dem
 * Profil kopiert (ProjectMasterData → `handleAddRepresentative`) und danach nie
 * wieder abgeglichen. Wurde ein Slot früher über das alte Stammdaten-Formular
 * mit getrennten Feldern für Name/E-Mail/Telefon besetzt und später auf eine
 * andere Person umgeschrieben, blieb die Nummer des Vorgängers stehen — im
 * Begehungs-PDF steht dann Name A mit der Telefonnummer von B (Projekt 6051,
 * 24.08.2026: Stellvertreter „Florian Schweinsteiger" mit der Mobilnummer von
 * Andreas Hüttel). Ohne Nachzug bleibt das für immer stehen, weil jedes weitere
 * Speichern den falschen Wert nur durchreicht.
 *
 * Konservativ mit Absicht:
 *   - Nur Einträge MIT id werden angefasst; Freitext-Vertreter ohne Profil
 *     bleiben unberührt.
 *   - Nur bei bestätigter Identität (`confirmsProfile`) — sonst würde eine
 *     falsch gestempelte id fremde Kontaktdaten ins Projekt schreiben.
 *   - Ein LEERES Feld im Profil überschreibt nichts. Eine von Hand gepflegte
 *     Durchwahl darf nicht verschwinden, nur weil das Profil keine trägt.
 *   - Der NAME bleibt unangetastet. Er ist das, was der Betreiber bewusst
 *     eingetragen hat; weicht er vom Profil ab, ist das ein Fall für die
 *     Diagnose, nicht für die stille Korrektur.
 *
 * Gibt `null` zurück, wenn nichts zu ändern ist. Das ist keine Bequemlichkeit,
 * sondern Pflicht: `applyRepresentatives` materialisiert bei Alt-Projekten das
 * `representatives`-Array, und dieser Backfill soll weiterhin nur bei einer
 * echten Änderung passieren — nicht bei jedem Projektaufruf.
 */
export const reconcileRepresentativeContacts = (
    metadata: ReportMetadata | undefined | null,
    profiles: RepresentativeProfile[] | undefined | null
): ReportMetadata | null => {
    if (!metadata || !profiles || profiles.length === 0) return null;

    const byId = new Map<string, RepresentativeProfile>();
    for (const profile of profiles) {
        const id = text(profile.id);
        if (id) byId.set(id, profile);
    }
    if (byId.size === 0) return null;

    const current = getRepresentatives(metadata);
    let changed = false;

    const next = current.map(rep => {
        const profile = byId.get(text(rep.id));
        if (!profile || !confirmsProfile(rep, profile)) return rep;

        const phone = text(profile.phone);
        const email = text(profile.email);
        const patch: ProjectRepresentative = { ...rep };
        if (phone && phone !== text(rep.phone)) { patch.phone = phone; changed = true; }
        if (email && email.toLowerCase() !== text(rep.email).toLowerCase()) { patch.email = email; changed = true; }
        return patch;
    });

    if (!changed) return null;
    return applyRepresentatives(metadata, next);
};
